/**
 * Build the description used for TrackingLinks auto-created for a PDK
 * Experience QR code.
 *
 * @param {Object} [opts={}] - Description fields.
 * @param {String} [opts.partnerSlug] - PDK partner slug.
 * @param {String} [opts.experienceName] - Experience name.
 * @returns {String} TrackingLink description, e.g. `PDK: pdk - Big Game`.
 */
export const buildPdkTrackingLinkDescription = ({
  partnerSlug,
  experienceName,
} = {}) => `PDK: ${partnerSlug || 'partner'} - ${experienceName || 'Experience'}`;

/**
 * Outcome of `ensurePdkQrCodeTrackingLink`. The status matters as much as the
 * rid: a caller editing an existing element has to tell "nothing to change"
 * apart from "the replacement we needed failed", or it can keep a stale rid
 * that no longer matches the element's destination.
 *
 * - `assigned`: a rid was obtained and should be written to the element.
 * - `unchanged`: the ensured rid is already on the element; leave it alone.
 * - `failed`: the ensure request failed or returned no rid (`rid` is null).
 * - `skipped`: nothing to ensure, e.g. no destination (`rid` is null).
 *
 * @typedef {Object} PdkTrackingLinkEnsureResult
 * @property {String|null} rid - Rid to assign, or null.
 * @property {String} status - One of `assigned`, `unchanged`, `failed`, `skipped`.
 */

/**
 * Ensure a PDK Experience QR code has a TrackingLink for its destination.
 *
 * The QR code renders the Experience's `qrCodeUrl`, which is the destination
 * scans should reach. The returned `rid` should be assigned to the element when
 * `status` is `assigned`; `unchanged` keeps the existing rid, while `failed`
 * means there is no usable replacement. Failures (including a timed-out
 * request) are logged and swallowed so they never block adding the QR code.
 *
 * @param {Object} opts - Ensure options.
 * @param {String} opts.destinationUrl - QR destination URL (`qrCodeUrl`).
 * @param {String} [opts.partnerSlug] - PDK partner slug.
 * @param {String} [opts.experienceName] - Experience name.
 * @param {String} [opts.currentTrackingLinkRid] - Rid already on the element.
 * @param {Function} opts.createOrReuseTrackingLink - API ensure function.
 * @returns {Promise<PdkTrackingLinkEnsureResult>} Rid and outcome status.
 */
export const ensurePdkQrCodeTrackingLink = async ({
  destinationUrl,
  partnerSlug,
  experienceName,
  currentTrackingLinkRid = null,
  createOrReuseTrackingLink,
} = {}) => {
  if (!destinationUrl || typeof createOrReuseTrackingLink !== 'function') {
    return { rid: null, status: 'skipped' };
  }

  try {
    const result = await createOrReuseTrackingLink({
      destinationUrl,
      description: buildPdkTrackingLinkDescription({ partnerSlug, experienceName }),
      type: 'partner',
    });
    const rid = result?.link?.rid;

    if (!rid) {
      return { rid: null, status: 'failed' };
    }

    if (rid === currentTrackingLinkRid) {
      return { rid, status: 'unchanged' };
    }

    return { rid, status: 'assigned' };
  } catch (err) {
    console.error('Failed to ensure PDK tracking link:', err);
    return { rid: null, status: 'failed' };
  }
};

/**
 * Decide which `trackingLinkRid` (if any) to write back onto a PDK Experience
 * QR code that is being re-configured from the element library.
 *
 * The edit path always updates the element's `permalink` to the newly chosen
 * destination, but `setManyElementValuesByProp` leaves untouched properties
 * alone. So when a replacement rid could not be ensured, the previous rid has
 * to be cleared explicitly — otherwise `Qrcode.vue` keeps rendering the old
 * tracking URL instead of the new destination. When the destination did not
 * actually change, the existing rid still matches and is left alone (no churn).
 *
 * @param {Object} opts - Update options.
 * @param {PdkTrackingLinkEnsureResult} [opts.result] - Ensure result.
 * @param {String} [opts.destinationUrl] - Destination now saved on the element.
 * @param {String} [opts.previousDestinationUrl] - Element's previous destination.
 * @param {String} [opts.currentTrackingLinkRid] - Rid currently on the element.
 * @returns {Object} Payload fragment (`{ trackingLinkRid }`) or an empty object
 *   when the element should be left untouched.
 */
export const resolveQrCodeTrackingLinkUpdate = ({
  result,
  destinationUrl,
  previousDestinationUrl,
  currentTrackingLinkRid = null,
} = {}) => {
  const { rid = null, status } = result || {};

  if (status === 'assigned' && rid) {
    return { trackingLinkRid: rid };
  }

  const destinationChanged = destinationUrl !== previousDestinationUrl;

  if (status === 'failed' && destinationChanged && currentTrackingLinkRid) {
    return { trackingLinkRid: null };
  }

  return {};
};

/**
 * Find QR code elements that don't have a TrackingLink selected.
 *
 * A QR code without a `trackingLinkRid` won't be attributed in analytics, so
 * the editor warns before saving.
 *
 * QR codes whose `permalink` is mapped from a data provider (e.g. a shoppable
 * product or a Google Sheet) derive their destination dynamically and cannot
 * use a static TrackingLink, so they are excluded from the warning.
 *
 * @param {Object} [elementsById={}] - Map of element id -> element.
 * @param {Function} [isMapped] - `(elementId, property) => boolean`; used to
 *   detect a data-mapped `permalink`.
 * @returns {Object[]} QR code elements missing a `trackingLinkRid`.
 */
// eslint-disable-next-line import/prefer-default-export
export const findQrCodesMissingTrackingLink = (
  elementsById = {},
  isMapped = () => false,
) => Object.values(elementsById).filter(
  (element) => element
      && element.type === 'qrcode'
      && !element.trackingLinkRid
      && !isMapped(element.id, 'permalink'),
);
